/**
 * BrandSelector — polished brand switcher for the sidebar (ads-iq parity).
 *
 * Replaces the old native <select>. Pill shows the current brand (avatar +
 * name) or "All brands"; clicking opens a popover with a search box, a
 * keyboard-navigable list (↑/↓ + Enter, Esc/click-outside to close), and a
 * "View all brands" footer. Reuses BrandAvatar for the deterministic colored
 * avatars so brand identity stays consistent across the app.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronsUpDown, Search, Check } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { BrandAvatar } from "@/components/BrandAvatar";
import { cn } from "@/lib/cn";

type BrandLite = { id: string; slug: string; name: string };

export function BrandSelector() {
  const { me } = useAuth();
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hoverIndex, setHoverIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const brands: BrandLite[] = me?.brands ?? [];

  const currentBrand = useMemo(
    () => brands.find((b) => b.slug === slug),
    [brands, slug],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter(
      (b) => b.name.toLowerCase().includes(q) || b.slug.toLowerCase().includes(q),
    );
  }, [brands, query]);

  useEffect(() => setHoverIndex(0), [query]);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 10);
      return () => clearTimeout(t);
    }
    setQuery("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(t) &&
        buttonRef.current &&
        !buttonRef.current.contains(t)
      ) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!me || brands.length === 0) return null;

  const selectBrand = (b: BrandLite) => {
    setOpen(false);
    navigate(`/brand/${b.slug}/dashboard`);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") return setOpen(false);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHoverIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHoverIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = filtered[hoverIndex];
      if (target) selectBrand(target);
    }
  };

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          "w-full flex items-center gap-2 rounded-md border border-border bg-bg-card px-2 py-1.5",
          "text-sm text-left hover:bg-bg-hover transition-colors",
        )}
      >
        {currentBrand ? (
          <BrandAvatar name={currentBrand.name} slug={currentBrand.slug} size="sm" />
        ) : (
          <span className="size-6 rounded bg-bg-subtle text-text-muted flex items-center justify-center text-xs font-bold shrink-0">
            A
          </span>
        )}
        <span className="flex-1 min-w-0 truncate font-medium text-text-primary">
          {currentBrand?.name ?? "All brands"}
        </span>
        <ChevronsUpDown size={14} className="text-text-muted shrink-0" />
      </button>

      {open && (
        <div
          ref={popoverRef}
          role="listbox"
          className="absolute left-0 right-0 top-full mt-1 z-50 rounded-lg border border-border-lighter bg-bg-card shadow-lg max-h-96 overflow-hidden flex flex-col"
        >
          <div className="relative px-2.5 pt-2.5 pb-2 border-b border-border-lighter">
            <Search
              size={13}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
            />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search brands…"
              className="w-full pl-7 pr-2 py-1.5 text-sm bg-bg-subtle rounded border border-transparent focus:border-accent/40 focus:outline-none focus:ring-2 focus:ring-accent/15"
            />
          </div>

          <div className="overflow-y-auto py-1">
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-xs text-text-muted">
                No brands match "{query}"
              </div>
            )}
            {filtered.map((b, idx) => {
              const isCurrent = currentBrand?.slug === b.slug;
              return (
                <button
                  key={b.id}
                  type="button"
                  onMouseEnter={() => setHoverIndex(idx)}
                  onClick={() => selectBrand(b)}
                  className={cn(
                    "w-full flex items-center gap-2 px-2.5 py-2 text-sm text-left transition-colors",
                    idx === hoverIndex ? "bg-bg-hover" : "hover:bg-bg-hover",
                  )}
                >
                  <BrandAvatar name={b.name} slug={b.slug} size="sm" />
                  <span className="flex-1 min-w-0">
                    <span className="block truncate text-text-primary">{b.name}</span>
                    <span className="block truncate text-[11px] text-text-muted">{b.slug}</span>
                  </span>
                  {isCurrent && <Check size={14} className="text-accent shrink-0" />}
                </button>
              );
            })}
          </div>

          <div className="border-t border-border-lighter px-2.5 py-2">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate("/dashboard");
              }}
              className="w-full text-xs text-text-muted hover:text-text-primary text-left"
            >
              View all brands →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
